import { IconNames } from '@blueprintjs/icons';
import { DateFormats, formatDate, sortAsc } from '@strata/helper-functions';
import { IconButton } from '@strata/react-buttons';
import { ActionButtonTextDropdownMenu } from '@strata/react-dropdown-menu';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { PageTitle } from '@strata/react-page-title';
import { Tag } from '@strata/react-tags';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { DeleteBatchDialog } from 'components/batchesAndScheduling/DeleteBatchDialog';
import { RenameBatchDialog } from 'components/batchesAndScheduling/RenameBatchDialog';
import { TreatmentIssuesIcon } from 'components/upcomingTreatments/TreatmentIssuesIcon';
import { PRODUCT_GO_LIVE_DATE_PREFIX } from 'services/goLiveFilterHelpers';
import { AppRoutes } from 'services/routes';
import { useGetBatchesAndFilteringCriteriaQuery } from 'store/api/goLiveApi';
import { useGetPayerSegmentsQuery } from 'store/api/referenceApi';
import { Grids, setGridActiveFilters } from 'store/grids/gridsSlice';

export const BatchesAndSchedulingPage = () => {
    const { data } = useGetBatchesAndFilteringCriteriaQuery();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [batchToDelete, setBatchToDelete] = useState(undefined);
    const [batchToRename, setBatchToRename] = useState(undefined);

    const handleNavigateWithFilters = (batch) => {
        const initialFilters = batch.filter_criteria.reduce((acc, tag) => {
            const key = tag.filter;
            if (key === 'is_ldd') {
                acc.ldd = [true];
            } else if (key === 'product_oid') {
                if (!acc.product_service_oid) acc.product_service_oid = [];
                acc.product_service_oid.push(tag.value);
            } else if (key === 'product_go_live_date') {
                if (!acc.go_live_date) acc.go_live_date = [];
                acc.go_live_date.push(`${PRODUCT_GO_LIVE_DATE_PREFIX}${tag.value}`);
            } else {
                if (!acc[key]) acc[key] = [];
                acc[key].push(tag.value);
            }
            return acc;
        }, {});

        dispatch(
            setGridActiveFilters({
                grid: Grids.UPCOMING_TREATMENTS,
                activeFilters: initialFilters,
            }),
        );

        navigate(AppRoutes.upcomingTreatments);
    };

    const batchOptions = data?.batches;
    const sortedBatches = useMemo(() => {
        if (!batchOptions) return [];

        return [...batchOptions].sort((a, b) => sortAsc(a.batch_name, b.batch_name));
    }, [batchOptions]);

    const { data: payerSegmentData } = useGetPayerSegmentsQuery();
    const { payerSegmentMap } = payerSegmentData ?? {};

    const getLabel = (tag) => {
        if (tag.filter === 'is_ldd') return 'Is LDD';
        if (tag.filter === 'current_segment_id') return payerSegmentMap?.[tag.value].name;
        if (tag.filter === 'go_live_date') return formatDate(tag.value, DateFormats.DATE);
        if (tag.filter === 'product_go_live_date')
            return `${formatDate(tag.value, DateFormats.DATE)} (Product Expansion)`;
        if (tag.filter === 'has_issues')
            return TreatmentIssuesIcon.TreatmentIssuesDisplayTextMap[tag.value];
        return tag.value;
    };

    return (
        <FormHeaderAndContent
            mainContent={
                <>
                    <PageTitle title="Batches" />
                    {sortedBatches.map((batch) => {
                        const menuOptions = [
                            {
                                text: 'Apply filters to Upcoming Treatments',
                                onItemClick: () => handleNavigateWithFilters(batch),
                            },
                        ];
                        return (
                            <div className="sds_batch-banner" key={batch.batch_id}>
                                <IconButton
                                    icon={IconNames.EDIT}
                                    title={`Rename ${batch.batch_name}`}
                                    onClick={() => setBatchToRename(batch)}
                                />
                                <span className="sds_batch-banner--title">{batch.batch_name}</span>
                                <span>({batch.treatment_count})</span>
                                {batch?.filter_criteria?.map((tag) => (
                                    <Tag key={tag.filter_criteria_id} label={getLabel(tag)} />
                                ))}
                                <div className="sds_batch-banner--actions">
                                    <ActionButtonTextDropdownMenu
                                        label="Actions"
                                        menuItems={menuOptions}
                                    />
                                    <IconButton
                                        icon={IconNames.TRASH}
                                        title={`Delete ${batch.batch_name}`}
                                        intent={IconButton.Intent.DANGER}
                                        onClick={() => setBatchToDelete(batch)}
                                    />
                                </div>
                            </div>
                        );
                    })}
                    <DeleteBatchDialog
                        isOpen={!!batchToDelete}
                        batch={batchToDelete}
                        onClose={() => setBatchToDelete(undefined)}
                    />
                    <RenameBatchDialog
                        isOpen={!!batchToRename}
                        batch={batchToRename}
                        onClose={() => setBatchToRename(undefined)}
                    />
                </>
            }
        />
    );
};
